'use client';

import type { BreadcrumbsProps as MantineBreadcrumbsProps } from '@mantine/core';
import { Breadcrumbs as MantineBreadcrumbs } from '@mantine/core';

import type { LockedProps } from '../types';

export type BreadcrumbsProps = Omit<MantineBreadcrumbsProps, LockedProps>;

/**
 * ### Breadcrumbs
 *
 * @description
 * Mantine's Breadcrumbs minus per-call-site style overrides (see {@link LockedProps}). Each child
 * is one crumb, separated by `separator` (`/` by default) — pass a `Link` for a navigable crumb and
 * a plain string for the current page.
 *
 * Two child gotchas: only `null` / `undefined` / booleans are dropped, so guard an optional string
 * crumb with `!!` (`''` would render an empty crumb *and* a separator); and Mantine injects the
 * crumb class into element children, so wrap a component that owns its own root styles
 * (`Tooltip`, `Popover`) in a `Box` to keep the class on the crumb.
 *
 * @example
 * ```tsx
 * <Breadcrumbs>
 *   <Link href="/user/organizations">Organizations</Link>
 *   {org?.name ?? 'Organization'}
 * </Breadcrumbs>
 * ```
 *
 * @example
 * ```tsx
 * <Breadcrumbs>
 *   <Link href="/admin">Admin</Link>
 *   {!!block?.name && <Text fw={600}>{block.name}</Text>}
 * </Breadcrumbs>
 * ```
 */
export const Breadcrumbs = (props: BreadcrumbsProps) => {
  return (
    // Mantine's root is a plain div; the landmark is what lets screen readers jump to the trail.
    <nav aria-label="Breadcrumb">
      <MantineBreadcrumbs {...props} />
    </nav>
  );
};
